'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'

import { CartContainer } from '../../brunswick/containers/cart'
import { useGoogleTagManager } from '../../lib/hooks/useGoogleTagManager'
import { useConsumeQueryParam } from '../_hooks/useConsumeQueryParam'

import { useLocalization } from './LocalizationProvider.client'

const useCartDialogImpl = () => {
  const { cart, trackCartOpened } = CartContainer.useContainer()
  const { region } = useLocalization()
  const { pushOpenMiniCart } = useGoogleTagManager()
  const [isOpen, setIsOpen] = useState(false)

  const pathname = usePathname()
  const previousPathnameRef = useRef(pathname)
  const pathnameChanged = previousPathnameRef.current !== pathname
  previousPathnameRef.current = pathname

  const searchParams = useSearchParams()
  const previousSearchParamsRef = useRef(searchParams)
  const searchParamsChanged = previousSearchParamsRef.current !== searchParams
  const hasOpenCartParam = searchParams?.get('openCart') === 'true'
  previousSearchParamsRef.current = searchParams

  // Close on navigation. Skip search-param changes while open so the
  // variant picker's late ?color/&size sync doesn't close the cart
  if ((pathnameChanged || (searchParamsChanged && !isOpen)) && !hasOpenCartParam) {
    setIsOpen(false)
  }

  const close = useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const open = useCallback(() => {
    setIsOpen(true)
    pushOpenMiniCart(cart, region.currency)
    trackCartOpened()
  }, [cart, pushOpenMiniCart, region.currency, trackCartOpened])

  useConsumeQueryParam({
    param: 'openCart',
    when: 'true',
    onMatch: open,
  })

  return {
    isOpen,
    close,
    open,
  }
}

const CartDialogContainer = createContainer(useCartDialogImpl)
CartDialogContainer.Provider.displayName = 'CartDialogProviderImpl'

const useCartInterstitialDialogImpl = () => {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previousPathnameRef = useRef(pathname)
  const previousSearchParamsRef = useRef(searchParams)

  if (
    previousPathnameRef.current !== pathname ||
    previousSearchParamsRef.current !== searchParams
  ) {
    previousPathnameRef.current = pathname
    previousSearchParamsRef.current = searchParams
    setIsOpen(false)
  }

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  return {
    isOpen,
    close,
    open,
  }
}

const CartInterstitialDialogContainer = createContainer(useCartInterstitialDialogImpl)
CartInterstitialDialogContainer.Provider.displayName = 'CartInterstitialDialogProviderImpl'

export const CartProvider = (props: { children?: React.ReactNode }) => {
  return (
    <CartContainer.Provider>
      <CartDialogContainer.Provider>
        <CartInterstitialDialogContainer.Provider>
          {props.children}
        </CartInterstitialDialogContainer.Provider>
      </CartDialogContainer.Provider>
    </CartContainer.Provider>
  )
}

export const useCartDialog = CartDialogContainer.useContainer
export const useCartInterstitialDialog = CartInterstitialDialogContainer.useContainer
export const useCart = CartContainer.useContainer
